/**
 * useNetworkStatus Hook
 *
 * Monitors network connectivity and provides offline-aware functionality.
 */

import { useState, useEffect, useCallback } from "react";

/**
 * Hook for monitoring network status
 * @returns {{ isOnline: boolean, wasOffline: boolean, checkConnection: Function }}
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    // Track that we came back online (useful for showing "reconnected" messages)
    if (!isOnline) {
      setWasOffline(true);
      // Clear the wasOffline flag after a delay
      setTimeout(() => setWasOffline(false), 5000);
    }
  }, [isOnline]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
  }, []);

  // Check actual connectivity (not just network interface)
  const checkConnection = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch("/manifest.json", {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setIsOnline(response.ok);
      return response.ok;
    } catch {
      setIsOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    checkConnection();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline, checkConnection]);

  return { isOnline, wasOffline, checkConnection };
};

export default useNetworkStatus;
